import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "../auth/AuthContext";

/**
 * Fetch-once reference data that survives a bad first attempt.
 *
 * The console is full of static lookups — route shapes, stops, the driver and
 * vehicle rosters — loaded in a mount effect with `[]` deps and a
 * `.catch(() => {})`. That shape has failed the same way four separate times:
 * the component mounts with the rest of the shell, before the session is
 * usable, the call 401s, the error is discarded, nothing ever retries, and the
 * feature is simply absent for the life of the page with nothing logged and no
 * way to distinguish it from "this agency has no data". Route lines vanished
 * from the live map for exactly this reason.
 *
 * This hook is the one place that pattern is implemented correctly:
 *
 *  - keyed on the access token, so signing in or refreshing reloads it;
 *  - retries a failure with backoff instead of giving up after one attempt;
 *  - keeps the last good value across a failed reload, so a blip does not
 *    blank a populated screen;
 *  - surfaces `error` rather than swallowing it, and logs it either way.
 *
 * It deliberately does not poll. For anything that changes on its own, use
 * `usePollingQuery`.
 */

const MAX_RETRIES = 3;
const RETRY_BASE_MS = 2000;

export interface ReferenceDataResult<T> {
  data: T | undefined;
  error: string | null;
  loading: boolean;
  /** Force a reload, e.g. from a "Retry" button. */
  reload: () => void;
}

export function useReferenceData<T>(
  fetcher: () => Promise<T>,
  options: {
    /** Used in the console warning when a load fails. */
    label: string;
    /** Re-fetch when any of these change (a service date, a selected route). */
    deps?: ReadonlyArray<unknown>;
    /** Skip fetching entirely while false (e.g. nothing selected yet). */
    enabled?: boolean;
  }
): ReferenceDataResult<T> {
  const { label, deps = [], enabled = true } = options;
  const { session } = useAuth();
  const accessToken = session?.accessToken;

  const [data, setData] = useState<T | undefined>();
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [attempt, setAttempt] = useState(0);

  // Held in a ref so changing the fetcher identity every render (the usual
  // case, since callers pass an inline closure) does not restart the effect.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const retryTimer = useRef<number | null>(null);
  const retriesUsed = useRef(0);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    setLoading(true);

    fetcherRef.current()
      .then((value) => {
        if (cancelled) return;
        setData(value);
        setError(null);
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        const message = err instanceof Error ? err.message : "Request failed";
        // Logged even when a retry is queued: a load that only succeeds on the
        // third go is worth seeing in the console.
        console.warn(`[referenceData] ${label} failed to load: ${message}`);
        setError(message);
        // Retries are counted within this effect run. A token change or a dep
        // change restarts the effect and gets a fresh budget, which is what we
        // want — those are new circumstances, not a continued failure.
        if (retriesUsed.current < MAX_RETRIES) {
          const wait = RETRY_BASE_MS * Math.pow(2, retriesUsed.current);
          retriesUsed.current += 1;
          retryTimer.current = window.setTimeout(() => {
            if (!cancelled) setAttempt((n) => n + 1);
          }, wait);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      if (retryTimer.current != null) {
        window.clearTimeout(retryTimer.current);
        retryTimer.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, enabled, attempt, ...deps]);

  // Reset the retry budget whenever the circumstances change, rather than on
  // each attempt (which would let it retry forever).
  useEffect(() => {
    retriesUsed.current = 0;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken, enabled, ...deps]);

  return { data, error, loading, reload };
}
